import { Form, Formik, FormikProps } from 'formik';
import { ReactElement, Ref, useEffect, useRef } from 'react';
import { AnyObject } from 'yup';
import OnboardFormLayout from '../../components/OnboardFormLayout';
import OptionalTextDivider from '../../components/OptionalTextDivider';
import { useDivideSchemaFields } from '../../hooks/useDivideSchemaFields';
import { BaseFormControls, SlotProps } from '../GenericForm';
import { useCRUDContext } from '../context/CRUDContext';
import { EntityIsNotSelected } from './components/EntityIsNotSelected';
import { FieldsArea } from './components/FieldsArea';
import { FormFieldType } from './components/FormFieldType';
import GenericFormField from './components/GenericFormField';
import { SubmitButton } from './components/SubmitButton';
import { fixedForwardRef } from './utils/forwardRefFix';
import { resetFormUsingKey } from './utils/resetFormUsingKey';
import { useFormControllerRefSetup } from './utils/useFormControllerRefSetup';

export interface DeleteFormProps<T extends AnyObject> extends SlotProps<T> {
  WarningElement?: ReactElement;
  submitCondition?: (values: T) => boolean;
}

function DeleteFormInner<T extends AnyObject>(
  props: DeleteFormProps<T>,
  ref?: Ref<BaseFormControls | null>
) {
  const crud = useCRUDContext();

  const formikRef = useRef<FormikProps<Partial<T>> | null>(null);
  useFormControllerRefSetup(ref, formikRef);

  // Reset form when entity changes
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm({ values: crud.currEntity });
    }
  }, [crud.currEntity, formikRef]);

  const { requiredFields, optionalFields } = useDivideSchemaFields(
    crud.yupSchemaOut
  );

  const renderFormFields = (fieldNames: string[]) =>
    fieldNames
      .filter((key) => !props?.hiddenFields?.includes(key))
      .map((key) => {
        const fieldProps = props.fieldsConfig?.[key] || {
          type: FormFieldType.TextDisplay,
          label: key,
          initialValue: '',
        };
        return <GenericFormField key={key} name={key} fieldMeta={fieldProps} />;
      });

  if (!crud.currEntity) {
    return <EntityIsNotSelected />;
  }

  const formKey = resetFormUsingKey(crud.currEntity);

  return (
    <Formik
      key={formKey}
      innerRef={formikRef}
      initialValues={crud.currEntity}
      validationSchema={crud.yupSchemaOut}
      onSubmit={async (value, helpers) =>
        crud.formSubmitHandler({
          helpers,
          operationCallback: async () => {
            if (props.handleSubmitValueTransformer) {
              return await crud.handleDelete(
                props.handleSubmitValueTransformer(value)
              );
            }
            if ('id' in value) {
              return await crud.handleDelete(value.id);
            }

            console.error(
              'DELETE FORM ERROR: field "id" is not present in object, specify submit handler in handleSubmitValueTransformer prop or check your entity'
            );
            throw new Error('Field "id" is not present in object');
          },
        })
      }
    >
      {(formikProps) => (
        <Form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <OnboardFormLayout
            onSubmit={() => {
              console.log('edge case submitting form');
            }}
          >
            <FieldsArea {...props.gridConfig}>
              {requiredFields.length > 0 && renderFormFields(requiredFields)}
            </FieldsArea>
            {optionalFields.length > 0 && <OptionalTextDivider />}
            <FieldsArea {...props.gridConfig}>
              {optionalFields.length > 0 && renderFormFields(optionalFields)}
            </FieldsArea>
            {props.WarningElement}
            {props.SubmitButton?.renderFn ? (
              props.SubmitButton.renderFn({
                formikProps: formikProps,
                config: props.SubmitButton?.config,
              })
            ) : (
              <SubmitButton
                formikProps={formikProps}
                config={props.SubmitButton?.config}
                disabled={
                  props.submitCondition
                    ? props.submitCondition(formikProps.values)
                    : false
                }
                sx={{
                  background: (theme) => theme.palette.error.main,
                  '&:hover': {
                    background: (theme) => theme.palette.error.dark,
                  },
                  '&.Mui-disabled': {
                    color: (theme) => theme.colors.common.white,
                    background: (theme) => theme.palette.error.light,
                    opacity: 0.6,
                  },
                }}
              />
            )}
          </OnboardFormLayout>
        </Form>
      )}
    </Formik>
  );
}

export const DeleteForm = fixedForwardRef(DeleteFormInner);
